import React, { useState } from 'react';
import PropTypes from 'prop-types';
import CustomRadio from '../../forms/inputs/CustomRadio';
import Anchor from '../../Anchor';
import StripeForm from '../../forms/inputs/StripeForm';
import RemoveModal from '../../../sections/profile/mainPanel/components/RemoveModal';
import translate from '../../translate';
import { useModal } from '../../../services/customHooks';
import StripeCardProp from '../../../sections/profile/propTypes/StripeCardProp';
import UserProp from '../../propTypes/userProp';

/**
 * Credit-card list used both in the order checkout (PaymentMethods) and on the
 * profile "Payment Options" page (PaymentForm). Renders the list of saved cards
 * with a radio selector, brand logo and a remove button on every card, plus a
 * "+ add new card" toggle that opens the StripeForm.
 *
 * Selection / add / remove are delegated to the parent via callbacks so each
 * context keeps its own semantics (checkout toggles a selection; the profile
 * page sets the default card). When `confirmOnRemove` is true a confirmation
 * dialog is shown before `onRemoveCard` is called.
 */
function CreditCardList({
  t,
  cards,
  selectedCreditCard,
  onSelectCard,
  onRemoveCard,
  onAddCard,
  confirmOnRemove,
  removeConfirmTitle,
  removeConfirmText,
  isCheckout,
  showSaveCard,
  isSubscription,
  isUserPlan,
  loggedUser,
}) {
  const [addingNewCard, setAddingNewCard] = useState(false);
  const [attachToUser, setAttachToUser] = useState(true);
  const [removeModal, setRemoveModal] = useModal({ show: false });

  const handleAttachToUser = () => setAttachToUser(prevState => !prevState);
  const handleCloseAddCard = () => setAddingNewCard(false);
  const handleOpenAddCard = () => setAddingNewCard(true);

  const handleFinishAddCard = async result => {
    try {
      await onAddCard({ ...result, attachToUser });
    } finally {
      handleCloseAddCard();
      setAttachToUser(true);
    }
  };

  const performRemove = cardId => {
    handleCloseAddCard();
    onRemoveCard(cardId);
  };

  const handleRemoveClick = cardId => () => {
    if (confirmOnRemove) {
      setRemoveModal({ show: true, data: { cardId } });
    } else {
      performRemove(cardId);
    }
  };

  const handleConfirmRemove = () => {
    const { cardId } = removeModal.data || {};
    if (cardId) {
      performRemove(cardId);
    }
    setRemoveModal({ show: false, data: {} });
  };

  return (
    <>
      <h3>{`${t('shop.cart.your_cards')}:`}</h3>
      <ul className="card-list">
        {cards.map((creditCard, idx) => (
          <li key={creditCard.id} className="credit-card-holder">
            <div className="form-group row">
              <div className="col-lg-9 col-md-9 credit-card-info row">
                <div className="col-form-label col-lg-6 col-12">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control,jsx-a11y/label-has-for */}
                  <label className="m-checkbox m-checkbox--state-success">
                    <CustomRadio
                      id={creditCard.id}
                      value={creditCard.id}
                      onClick={() => onSelectCard(creditCard)}
                      checked={creditCard.id === selectedCreditCard}
                    />
                    <strong>{`**** **** **** ${creditCard.last4}`}</strong>
                    <br />
                    {`${t('expires_at')} ${creditCard.exp_month}/${
                      creditCard.exp_year
                    }`}
                    <span className="span-checkbox" />
                  </label>
                </div>
                <div className="credit-card-logo col-lg-4 col-6">
                  <img alt={`${creditCard.brand} logo`} src={creditCard.logo} />
                </div>
              </div>
              <div className="col-lg-3 col-md-3 remove-credit-card col-6">
                <div className="profile-actions">
                  {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
                  <a
                    role="button"
                    tabIndex={idx}
                    onClick={handleRemoveClick(creditCard.id)}
                    className="btn btn-metal m-btn m-btn--icon
                              m-btn--icon-only m-btn--custom m-btn--pill"
                  >
                    <i className="fa fa-trash-alt" />
                  </a>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
      {!addingNewCard ? (
        <div className="add-new-btn">
          <Anchor
            onClick={handleOpenAddCard}
            className="btn btn-outline-metal m-btn m-btn--icon m-btn--icon-only
                       m-btn--custom m-btn--outline-2x m-btn--pill m-btn--air"
          >
            <i className="la la-plus" />
            <span>{t('add_card_payment')}</span>
          </Anchor>
        </div>
      ) : (
        <div className="add-new-address">
          <StripeForm
            onFinish={handleFinishAddCard}
            onCancel={handleCloseAddCard}
            isCheckout={isCheckout}
            attachToUser={attachToUser}
            onChangeAttachToUser={handleAttachToUser}
            showSaveCard={showSaveCard}
            isSubscription={isSubscription}
            loggedUser={loggedUser}
            isUserPlan={isUserPlan}
          />
        </div>
      )}
      {removeModal.show && (
        <RemoveModal
          title={removeConfirmTitle}
          contentText={removeConfirmText}
          index={0}
          onRemove={handleConfirmRemove}
          onClose={() => setRemoveModal({ show: false, data: {} })}
        />
      )}
    </>
  );
}

CreditCardList.propTypes = {
  t: PropTypes.func.isRequired,
  cards: PropTypes.arrayOf(PropTypes.shape(StripeCardProp.propType)),
  selectedCreditCard: PropTypes.string,
  onSelectCard: PropTypes.func.isRequired,
  onRemoveCard: PropTypes.func.isRequired,
  onAddCard: PropTypes.func.isRequired,
  confirmOnRemove: PropTypes.bool,
  removeConfirmTitle: PropTypes.string,
  removeConfirmText: PropTypes.string,
  isCheckout: PropTypes.bool,
  showSaveCard: PropTypes.bool,
  isSubscription: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  isUserPlan: PropTypes.bool,
  loggedUser: PropTypes.shape(UserProp.propType),
};

CreditCardList.defaultProps = {
  cards: [],
  selectedCreditCard: '',
  confirmOnRemove: false,
  removeConfirmTitle: '',
  removeConfirmText: '',
  isCheckout: false,
  showSaveCard: true,
  isSubscription: false,
  isUserPlan: false,
  loggedUser: null,
};

export default translate(CreditCardList);
